import { useState } from 'react';
import { EnrichmentResult } from '../types';
import {
  getEnrichmentPayload,
  createEnrichmentInfoContent,
} from '../../../components/AdvancedSearch/helpers/helpers';
import { trackEnrichmentAction } from '../../../utils/analytics';

interface UseEnrichmentAPIProps {
  authorization: string | any;
  isAuthenticated: boolean;
  enrichmentParams: any;
}

export const useEnrichmentAPI = ({
  authorization,
  isAuthenticated,
  enrichmentParams,
}: UseEnrichmentAPIProps) => {
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [enrichmentResults, setEnrichmentResults] = useState<EnrichmentResult[] | null>(null);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [showEmptyResultsAlert, setShowEmptyResultsAlert] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showApiKeyAlert, setShowApiKeyAlert] = useState(false);
  const [showTrialLimitModal, setShowTrialLimitModal] = useState(false);

  const performEnrichment = async (
    searchType: string,
    text: string,
    geneFilterType: string,
    paramsOverride?: typeof enrichmentParams,
  ) => {
    if (!isAuthenticated) {
      setShowApiKeyAlert(true);
      return;
    }

    const paramsToUse = paramsOverride ?? enrichmentParams;

    setEnrichmentLoading(true);
    setEnrichmentResults(null);
    setShowErrorAlert(false);
    setShowEmptyResultsAlert(false);
    setShowSuccessAlert(false);

    try {
      trackEnrichmentAction('enrichment_started', `${searchType}_${text.split('\n').length}_items`);

      const formatFilterType =
        searchType === 'GENES'
          ? (geneFilterType.replace('geneSymbol', 'symbol').replace('name', 'symbol') as
              | 'symbol'
              | 'ensemblId'
              | 'geneId'
              | 'uniprotId')
          : 'strID';

      const payload = getEnrichmentPayload(searchType, text, paramsToUse, formatFilterType as any);

      const baseUrl = `${process.env.REACT_APP_URL_BACKEND_API}api/v1`;
      const endpoint = `${baseUrl}/enrichment/${searchType === 'GENES' ? 'gene' : 'variant'}`;

      const baseHeaders = {
        'Content-Type': 'application/json',
        Authorization: authorization,
      };

      const [csvResponse, jsonResponse] = await Promise.all([
        fetch(endpoint, {
          method: 'POST',
          headers: { ...baseHeaders, Accept: 'text/csv' },
          body: JSON.stringify(payload),
        }),
        fetch(endpoint, {
          method: 'POST',
          headers: { ...baseHeaders, Accept: 'application/json' },
          body: JSON.stringify(payload),
        }),
      ]);

      if (csvResponse.status === 402 || jsonResponse.status === 402) {
        setShowTrialLimitModal(true);
        return;
      }

      if (!csvResponse.ok || !jsonResponse.ok) {
        throw new Error(`Enrichment request failed: CSV ${csvResponse.status}, JSON ${jsonResponse.status}`);
      }

      const csvText = await csvResponse.text();
      const jsonData = await jsonResponse.json();

      if (jsonData.status === 'BAD_REQUEST' || jsonData.error) {
        const errorMessage = jsonData.error?.message || jsonData.error?.details || 'Unknown error';
        throw new Error(errorMessage);
      }

      if (!jsonData.payload || jsonData.payload.length === 0) {
        setShowEmptyResultsAlert(true);
        trackEnrichmentAction('enrichment_empty', `${searchType}_no_results`);
        return;
      }

      setEnrichmentResults(jsonData.payload);
      setShowSuccessAlert(true);

      trackEnrichmentAction('enrichment_completed', `${searchType}_${jsonData.payload?.length || 0}_results`);

      const metadataContent = createEnrichmentInfoContent({
        searchType,
        text,
        enrichmentParams: paramsToUse,
        geneFilterType,
      });

      return {
        enrichmentData: jsonData.payload,
        csvData: csvText,
        metadataData: metadataContent,
      };
    } catch (error) {
      console.error('Enrichment error:', error);
      setShowErrorAlert(true);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      trackEnrichmentAction('enrichment_failed', `${searchType}_${errorMessage}`);
    } finally {
      setEnrichmentLoading(false);
    }
  };

  const clearResults = () => {
    setEnrichmentResults(null);
    setShowSuccessAlert(false);
    setShowErrorAlert(false);
    setShowEmptyResultsAlert(false);
  };

  const dismissAlerts = () => {
    setShowSuccessAlert(false);
    setShowErrorAlert(false);
    setShowEmptyResultsAlert(false);
  };

  return {
    enrichmentLoading,
    enrichmentResults,
    showErrorAlert,
    showEmptyResultsAlert,
    showSuccessAlert,
    showApiKeyAlert,
    showTrialLimitModal,
    performEnrichment,
    clearResults,
    dismissAlerts,
    setShowApiKeyAlert,
    setShowTrialLimitModal,
  };
};
